import React, {
  createContext, useContext, useMemo, useState,
} from 'react';
import { Image, Modal } from 'react-bootstrap';
import CustomModal from '../ui/CustomModal';
import { APP_STORE_DOWNLOAD_URL, GOOGLE_PLAY_DOWNLOAD_URL } from '../../constants';
import AppStoreImage from '../../images/app-store-badge.png';
import PlayStoreImage from '../../images/google-play-badge.png';

const storeBadgeImageStyle = {
  border: '1px solid white',
  borderRadius: 10,
  width: 165,
  height: 50,
};

type WebsiteShutdownNoticeContextValue = { showNotice: () => void };

const WebsiteShutdownNoticeContext = createContext<WebsiteShutdownNoticeContextValue>({
  showNotice: () => { },
});

export const useWebsiteShutdownNotice = () => useContext(WebsiteShutdownNoticeContext);

type WebsiteShutdownNoticeModalProps = { show: boolean, onHide: () => void };

function WebsiteShutdownNoticeModal({ show, onHide }: WebsiteShutdownNoticeModalProps) {
  return (
    <CustomModal show={show} onHide={onHide} centered aria-labelledby="website-shutdown-notice-title">
      <Modal.Header className="border-0 shadow-none justify-content-end" closeButton />
      <Modal.Body className="text-center pt-0 px-4 pb-4">
        <h1 id="website-shutdown-notice-title" className="h3 text-primary mb-3">Notice</h1>
        <p>
          We have released a major update for Slasher, which is only available on the Slasher
          mobile app. Slasher can no longer be accessed via the website until further notice.
        </p>
        <p>To access Slasher, please download the Slasher app on Android or iOS.</p>
        <p className="mb-2">Click here to download Slasher for Android:</p>
        <a href={GOOGLE_PLAY_DOWNLOAD_URL} target="_blank" rel="noreferrer">
          <Image style={storeBadgeImageStyle} src={PlayStoreImage} alt="Get it on Google Play" />
        </a>
        <p className="mt-4 mb-2">Click here to download Slasher for iOS:</p>
        <a href={APP_STORE_DOWNLOAD_URL} target="_blank" rel="noreferrer">
          <Image style={storeBadgeImageStyle} src={AppStoreImage} alt="Download on the App Store" />
        </a>
      </Modal.Body>
    </CustomModal>
  );
}

type WebsiteShutdownNoticeProviderProps = { initiallyOpen: boolean, children: React.ReactNode };

export function WebsiteShutdownNoticeProvider(
  { initiallyOpen, children }: WebsiteShutdownNoticeProviderProps,
) {
  const [show, setShow] = useState(initiallyOpen);
  const value = useMemo(() => ({ showNotice: () => setShow(true) }), []);

  return (
    <WebsiteShutdownNoticeContext.Provider value={value}>
      {children}
      <WebsiteShutdownNoticeModal show={show} onHide={() => setShow(false)} />
    </WebsiteShutdownNoticeContext.Provider>
  );
}
